import { Dialog, Button } from "@endya-conformite/ui-kit";
import WarningIcon from "@endya-conformite/ui-kit/src/assets/icons/exclamation-circle-fill.svg?react";
import RedirectIcon from "@endya-conformite/ui-kit/src/assets/icons/redirect.svg?react";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { useCreateRemediationMutation } from "@src/data/controle-quinquennal/hook/useCreateRemediationMutation";
import { useUpdateRemediationMutation } from "@src/data/controle-quinquennal/hook/useUpdateRemediationMutation";
import {
  REMEDIATION_TAB_PAYLOAD,
  REMEDIATION_TAB_READY,
} from "@src/pages/controle-quinquennal/CQRemediationPage";
import classNames from "classnames";
import {
  MutableRefObject,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./CQRemediationDialog.module.scss";
import {
  CQRemediationForm,
  CQRemediationFormValues,
} from "./CQRemediationForm";

type DiscardReason = "navigation" | "cancel";

const DISCARD_MESSAGES: Record<
  DiscardReason,
  Record<"description" | "files" | "both", string>
> = {
  navigation: {
    description:
      "Vous êtes sur le point de quitter la page. Votre commentaire ne sera pas enregistré. Êtes-vous sûr de vouloir quitter ?",
    files:
      "Vous êtes sur le point de quitter la page. Vos pièces jointes ne seront pas enregistrées. Êtes-vous sûr de vouloir quitter ?",
    both: "Vous êtes sur le point de quitter la page. Votre commentaire et vos pièces jointes ne seront pas enregistrés. Êtes-vous sûr de vouloir quitter ?",
  },
  cancel: {
    description:
      "Votre commentaire ne sera pas enregistré. Êtes-vous sûr de vouloir annuler ?",
    files:
      "Vos pièces jointes ne seront pas enregistrées. Êtes-vous sûr de vouloir annuler ?",
    both: "Votre commentaire et vos pièces jointes ne seront pas enregistrés. Êtes-vous sûr de vouloir annuler ?",
  },
};

const getDiscardMessage = (
  reason: DiscardReason,
  dirty: { description: boolean; files: boolean }
): string => {
  if (dirty.description && dirty.files) return DISCARD_MESSAGES[reason].both;
  if (dirty.files) return DISCARD_MESSAGES[reason].files;
  return DISCARD_MESSAGES[reason].description;
};

const LEAVE_BUTTON_LABELS: Record<DiscardReason, string> = {
  navigation: "Quitter sans enregistrer",
  cancel: "Supprimer et fermer",
};

const FORM_ID = "cq-remediation-form";

export type CQRemediationDialogHandle = {
  triggerDiscardWarning: (reason: DiscardReason) => void;
  isDirty: () => boolean;
  isShowingWarning: () => boolean;
  expand: () => void;
};

type CQRemediationDialogProps = {
  onClose: (created: boolean, detachedValues?: CQRemediationFormValues) => void;
  dialogRef: MutableRefObject<HTMLDialogElement | null>;
  onDirtyChange?: (dirty: { description: boolean; files: boolean }) => void;
  idCQ: string;
  idAxe: string;
  idEntreprise: string;
  idRemediation?: string;
  initialValues?: CQRemediationFormValues;
};

type FooterActionsProps = {
  showDiscardWarning: boolean;
  discardReason: DiscardReason;
  isPending: boolean;
  onBack: () => void;
  onLeave: () => void;
  onCancel: () => void;
};

function FooterActions({
  showDiscardWarning,
  discardReason,
  isPending,
  onBack,
  onLeave,
  onCancel,
}: FooterActionsProps) {
  if (showDiscardWarning) {
    return (
      <>
        <Button variant="outline" onClick={onBack}>
          Retour
        </Button>
        <Button
          className={styles.leaveButton}
          variant="filled"
          onClick={onLeave}
        >
          {LEAVE_BUTTON_LABELS[discardReason]}
        </Button>
      </>
    );
  }

  return (
    <>
      <Button variant="outline" onClick={onCancel}>
        Annuler
      </Button>
      <Button
        type="submit"
        form={FORM_ID}
        disabled={isPending}
        icon={isPending ? <Loader /> : undefined}
      >
        Valider
      </Button>
    </>
  );
}

export const CQRemediationDialog = forwardRef<
  CQRemediationDialogHandle,
  CQRemediationDialogProps
>(function CQRemediationDialog(
  {
    onClose,
    dialogRef,
    onDirtyChange,
    idCQ,
    idAxe,
    idEntreprise,
    idRemediation,
    initialValues,
  },
  ref
) {
  const isEdit = Boolean(idRemediation);
  const createMutation = useCreateRemediationMutation(idEntreprise);
  const updateMutation = useUpdateRemediationMutation(idEntreprise);
  const isPending = isEdit
    ? updateMutation.isPending
    : createMutation.isPending;
  const [dirtyFields, setDirtyFields] = useState({
    description: false,
    files: false,
  });
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [discardReason, setDiscardReason] = useState<DiscardReason>("cancel");
  const initialFormValuesRef = useRef<CQRemediationFormValues>(
    initialValues ?? { files: [], description: "" }
  );
  const [formValues, setFormValues] = useState<CQRemediationFormValues>(
    initialFormValuesRef.current
  );
  const dialogExpandRef = useRef<(() => void) | null>(null);
  const isDirty = dirtyFields.description || dirtyFields.files;

  useImperativeHandle(
    ref,
    () => ({
      triggerDiscardWarning: (reason: DiscardReason) => {
        dialogExpandRef.current?.();
        setDiscardReason(reason);
        setShowDiscardWarning(true);
      },
      isDirty: () => isDirty,
      isShowingWarning: () => showDiscardWarning,
      expand: () => dialogExpandRef.current?.(),
    }),
    [isDirty, showDiscardWarning]
  );

  const handleDirtyChange = useCallback(
    (dirty: { description: boolean; files: boolean }) => {
      setDirtyFields(dirty);
      onDirtyChange?.(dirty);
    },
    [onDirtyChange]
  );

  const handleSubmit = async (values: CQRemediationFormValues) => {
    const filesToAdd = values.files.filter((f) => f.file);
    const idFiles = values.files
      .filter((f) => f.idFile && !f.file)
      .map((f) => f.idFile as string);

    if (isEdit && idRemediation) {
      await updateMutation.mutateAsync({
        idControleQuinquennal: idCQ,
        idAxe,
        idRemediation,
        payload: {
          description: values.description,
          idFiles,
        },
        files: filesToAdd.map((f) => f.file) as File[],
      });
    } else {
      await createMutation.mutateAsync({
        idControleQuinquennal: idCQ,
        idAxe,
        payload: {
          description: values.description,
          idFiles,
        },
        files: filesToAdd.map((f) => f.file) as File[],
      });
    }
    onClose(true);
  };

  const handleCancel = () => {
    if (!isDirty) {
      onClose(false);
    } else {
      dialogExpandRef.current?.();
      setDiscardReason("cancel");
      setShowDiscardWarning(true);
    }
  };

  const handleOpenInNewTab = () => {
    const url = `/controle-quinquennal/${idEntreprise}/${idCQ}/${idAxe}/remediation?transfer=1${
      idRemediation ? `&idRemediation=${idRemediation}` : ""
    }`;
    const targetWindow = window.open(url, "_blank");
    if (!targetWindow) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== targetWindow) return;
      const data = event.data as { type?: string } | null | undefined;
      if (!data || data.type !== REMEDIATION_TAB_READY) return;
      targetWindow.postMessage(
        {
          type: REMEDIATION_TAB_PAYLOAD,
          payload: formValues,
        },
        window.location.origin
      );
      window.removeEventListener("message", handleMessage);
      onClose(false, formValues);
    };

    window.addEventListener("message", handleMessage);
  };

  return (
    <Dialog
      className={classNames({
        [styles.discardWarningActive]: showDiscardWarning,
      })}
      dialogRef={dialogRef}
      variant="floating"
      resizable
      disableMinimize={showDiscardWarning}
      expandTriggerRef={dialogExpandRef}
    >
      <div className={styles.header}>
        <h4>Commentaire de remédiation</h4>
        {!showDiscardWarning && (
          <Button
            variant="text"
            icon={<RedirectIcon />}
            onClick={handleOpenInNewTab}
            aria-label="Ouvrir dans un nouvel onglet"
          />
        )}
      </div>

      <div className={styles.body}>
        {showDiscardWarning && (
          <div className={styles.confirmDiscardMessage}>
            <WarningIcon
              width={16}
              height={16}
              className={styles.warningIcon}
            />
            {getDiscardMessage(discardReason, dirtyFields)}
          </div>
        )}
        {!showDiscardWarning && (
          <CQRemediationForm
            formId={FORM_ID}
            onSubmit={handleSubmit}
            onDirtyChange={handleDirtyChange}
            onValuesChange={setFormValues}
            initialValues={initialFormValuesRef.current}
          />
        )}
      </div>
      <div className={styles.footer}>
        <FooterActions
          showDiscardWarning={showDiscardWarning}
          discardReason={discardReason}
          isPending={isPending}
          onBack={() => setShowDiscardWarning(false)}
          onLeave={() => onClose(false)}
          onCancel={handleCancel}
        />
      </div>
    </Dialog>
  );
});
